import { PRODUCT_GROUP, PRIME } from "@/constants/productKey";
import { PaymentOptionKey } from "@/constants/paymentOptions";
import { primeCouponCode } from "@/constants/couponCode";
import { GOI_PLATFORM_URL, PACKAGE_KEY } from "goi_common";

// landingFunnelKey SSOT: goi_common
export { landingFunnelKey } from "goi_common";
import { landingFunnelKey } from "goi_common";

interface ProductKeys {
  monthly?: string; // 월납 100원
  single?: string; // 일시납 3만원
  saving?: string; // 적금 9900원
}

interface CouponValue {
  key: string; // 쿠폰 코드
  description: string; // 면제 안내 문구 (\n으로 줄바꿈)
}

export interface CouponKeys {
  monthly?: CouponValue; // 월납 100원 결제 시
  saving?: CouponValue; // 적금 9900원 결제 시
}

/**
 * 퍼널 설정 관리
 *
 * ## 템플릿 패턴
 * - 동일한 설정이 3개 이상 반복될 때 템플릿 생성 (CLAUDE.md 3번 규칙)
 * - 새 퍼널 추가 시 기존 템플릿 활용 가능한지 확인
 * - 결제 옵션 제한: payment.options 추가 (없으면 DEFAULT_PAYMENT_OPTIONS 사용)
 */

interface FunnelDetailValue {
  paths: {
    home: string;
    info: string;
    "info-trust"?: string;
    verification: string;
    "product-intro"?: string;
  };
  product: {
    type: string;
    code: string;
    keys?: ProductKeys;
    coupon?: CouponKeys;
  };
  payment?: {
    options: PaymentOptionKey[];
  };
}

// 퍼널 템플릿 - 반복되는 설정 추출
const PROMOTION_100_TEMPLATE: FunnelDetailValue = {
  paths: {
    home: "", // 개별 설정 필요
    info: "/registration/info",
    "info-trust": "/registration/info",
    verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}`,
  },
  product: {
    type: "PROMOTION",
    code: PRODUCT_GROUP["100"],
  },
};

const PROMOTION_3D3_TEMPLATE: FunnelDetailValue = {
  paths: {
    home: "", // 개별 설정 필요
    info: `/registration/verification/?productGroup=${PRODUCT_GROUP["3-d-3"]}`,
    "info-trust": `/registration/verification/?productGroup=${PRODUCT_GROUP["3-d-3"]}`,
    verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["3-d-3"]}`,
  },
  product: {
    type: "PROMOTION",
    code: PRODUCT_GROUP["3-d-3"],
  },
};

// const PROMOTION_9900_TEMPLATE: FunnelDetailValue = {
//   paths: {
//     home: "", // 개별 설정 필요
//     info: `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}`,
//     "info-trust": `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}`,
//     verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}`,
//   },
//   product: {
//     type: "PROMOTION",
//     code: PRODUCT_GROUP["9900"],
//   },
// };

export const funnelDetailValues: Record<string, FunnelDetailValue> = {
  [landingFunnelKey.GROUP[100]]: {
    paths: {
      home: "/",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["100"]}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP["100"],
      keys: {
        monthly: PACKAGE_KEY.PRIME[289].M1,
        single: PACKAGE_KEY.PRIME[289].S1,
        saving: PRIME["289"].MS99DC2D97MT,
      },
    },
  },

  [landingFunnelKey.GROUP["private_thanks"]]: {
    paths: {
      home: "/private-thanks",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["conducted"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["conducted"]}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP.adv,
    },
  },

  [landingFunnelKey.GROUP["adv"]]: {
    paths: {
      home: "/adv",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.adv}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP.adv,
    },
  },

  // PROMOTION_100_TEMPLATE 사용
  [landingFunnelKey.GROUP["promotion_25_i"]]: {
    ...PROMOTION_100_TEMPLATE,
    paths: { ...PROMOTION_100_TEMPLATE.paths, home: "/promotion/25-i" },
  },

  [landingFunnelKey.GROUP["promotion_25_i_t"]]: {
    paths: {
      home: "/promotion/25-i-t",
      info: "/registration/toss/info",
      "info-trust": "/registration/toss/info",
      verification: `/registration/toss/verification/?productGroup=${PRODUCT_GROUP["100"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["100"],
    },
  },

  [landingFunnelKey.GROUP["promotion_25_j"]]: {
    ...PROMOTION_100_TEMPLATE,
    paths: { ...PROMOTION_100_TEMPLATE.paths, home: "/promotion/25-j" },
  },

  [landingFunnelKey.GROUP["promotion_25_k"]]: {
    ...PROMOTION_100_TEMPLATE,
    paths: { ...PROMOTION_100_TEMPLATE.paths, home: "/promotion/25-k" },
  },

  [landingFunnelKey.GROUP["promotion_25_l"]]: {
    ...PROMOTION_100_TEMPLATE,
    paths: { ...PROMOTION_100_TEMPLATE.paths, home: "/promotion/25-l" },
  },

  [landingFunnelKey.GROUP["promotion_25_m"]]: {
    ...PROMOTION_100_TEMPLATE,
    paths: { ...PROMOTION_100_TEMPLATE.paths, home: "/promotion/25-m" },
  },
  // PROMOTION_3D3_TEMPLATE 사용
  [landingFunnelKey.GROUP["promotion_25_3d3"]]: {
    ...PROMOTION_3D3_TEMPLATE,
    paths: { ...PROMOTION_3D3_TEMPLATE.paths, home: "/promotion/25-3d3" },
    payment: {
      options: ["100", "saving_tdt"] as PaymentOptionKey[],
    },
  },

  [landingFunnelKey.GROUP["promotion_25_3d3_2"]]: {
    ...PROMOTION_3D3_TEMPLATE,
    paths: { ...PROMOTION_3D3_TEMPLATE.paths, home: "/promotion/25-3d3-n2" },
    payment: {
      options: ["100", "saving_tdt"] as PaymentOptionKey[],
    },
  },

  // 삼쩜삼 9월 프로모션(/promotion/25-3d3).
  // 퍼널키 이름은 최초 첫달무료(mt_zero) 시절 그대로지만, 현재 혜택은
  // 랜딩 고지에 맞춰 9,900원 뒷 3개월 면제 + 10만원 할인이다. (첫 1개월 면제 쿠폰 제거)
  // 퍼널키는 집계 연속성 때문에 변경하지 않는다.
  [landingFunnelKey.GROUP["promotion_25_3d3_mt_zero"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["3-d-3"]}&fnk=${landingFunnelKey.GROUP["promotion_25_3d3_mt_zero"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["3-d-3"]}&fnk=${landingFunnelKey.GROUP["promotion_25_3d3_mt_zero"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["3-d-3"],
      keys: {
        monthly: PRIME["289"].M1MTTDT,
        saving: PRIME["289"].MS99DC12D97TDT,
      },
    },
    payment: {
      options: ["100", "saving_tdt"] as PaymentOptionKey[],
    },
  },

  [landingFunnelKey.GROUP["toss_100"]]: {
    paths: {
      home: "/toss",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}&fnk=toss`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["100"]}&fnk=toss`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP["100"],
    },
  },
  [landingFunnelKey.GROUP["promotion_9900"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["9900"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["9900"],
    },
    payment: {
      options: ["saving"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["hbf_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["hbf"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["hbf"],
    },
    payment: {
      options: ["100", "saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["hbf_9900"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["hbf"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["hbf"],
    },
    payment: {
      options: ["saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["hbf_mixed_v2"]]: {
    paths: {
      home: "/promotion/event/26-hbf",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["hbf"]}&fnk=${landingFunnelKey.GROUP["hbf_mixed_v2"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["hbf"],
      keys: {
        saving: PRIME["289"].MS99DC12D97HBF,
      },
    },
    payment: {
      options: ["100", "saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["hbf_mixed_mt"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["hbf"]}&fnk=${landingFunnelKey.GROUP["hbf_mixed_mt"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["hbf"],
      keys: {
        monthly: PRIME["289"].M1MTHBF,
        single: PRIME["289"].S1MTHBF,
        saving: PRIME["289"].MS99DC2D97MTHBF,
      },
    },
    payment: {
      options: ["100", "saving_mt"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["memorial_table"]]: {
    paths: {
      home: "/memorialtable",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["memorial_table"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["memorial_table"]}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP.mt,
      keys: {
        saving: PRIME["289"].MS99MT,
      },
    },
    payment: {
      options: ["saving_no_discount"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["memorial_table_my_v2"]]: {
    paths: {
      home: `${GOI_PLATFORM_URL}/my/v3`,
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["memorial_table_my_v2"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["memorial_table_my_v2"]}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP.mt,
      keys: {
        single: PRIME["289"].S1MT,
      },
    },
    payment: {
      options: ["single_mt_my_v2"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["memorial_table_goodfuneral"]]: {
    paths: {
      home: "/message/good-funeral",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["memorial_table_goodfuneral"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["memorial_table_goodfuneral"]}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP.mt,
      keys: {
        single: PRIME["289"].S1MT,
      },
    },
    payment: {
      options: ["single_mt_my_v2"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_c"]]: {
    paths: {
      home: "/promotion/26-c",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_c"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_c"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        saving: PRIME["279"].MS99MT,
      },
    },
    payment: {
      options: ["saving_no_discount_promotion_26c"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_d"]]: {
    paths: {
      home: "/promotion/26-d",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}&fnk=${landingFunnelKey.GROUP["promotion_26_d"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["9900"]}&fnk=${landingFunnelKey.GROUP["promotion_26_d"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["9900"],
      keys: {
        saving: PRIME["289"].MS99DC2D97,
      },
    },
    payment: {
      options: ["saving_promotion_26_d"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_up_a"]]: {
    paths: {
      home: "/promotion/26-up-a",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_up_a"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_up_a"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        saving: PRIME["259"].MS99MT,
      },
    },
    payment: {
      options: ["saving_promotion_26_up_a"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_up_b"]]: {
    paths: {
      home: "/promotion/26-up-b",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_up_b"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_up_b"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        saving: PRIME["279"].MS99MT,
      },
    },
    payment: {
      options: ["saving_promotion_26_up_b"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_up_c"]]: {
    paths: {
      home: "/promotion/26-up-c",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_up_c"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_26_up_c"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        saving: PRIME["279"].MS99DC15,
      },
    },
    payment: {
      options: ["saving_promotion_26_up_c"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_hp"]]: {
    paths: {
      home: "/promotion/26-hp",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.hp}&fnk=${landingFunnelKey.GROUP.promotion_26_hp}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.hp}&fnk=${landingFunnelKey.GROUP.promotion_26_hp}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.hp,
      keys: {
        monthly: PRIME["289"].M1HP,
        single: PRIME["289"].S1HP,
        saving: PRIME["289"].MS99DC12D97HP,
      },
    },
    payment: {
      options: ["100", "saving_promotion_26_hp"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_e"]]: {
    paths: {
      home: "/promotion/26-e",
      info: `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}&fnk=${landingFunnelKey.GROUP.promotion_26_e}`,
      "info-trust": `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}&fnk=${landingFunnelKey.GROUP.promotion_26_e}`,
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["9900"]}&fnk=${landingFunnelKey.GROUP.promotion_26_e}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["9900"]}&fnk=${landingFunnelKey.GROUP.promotion_26_e}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["9900"],
      keys: {
        saving: PRIME["289"].MS99DC2D97N3,
      },
    },
    payment: {
      options: ["saving"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_f"]]: {
    paths: {
      home: "/promotion/26-f",
      info: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_26_f}`,
      "info-trust": `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_26_f}`,
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_26_f}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_26_f}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["100"],
      keys: {
        single: PRIME["289"].S1N1,
      },
    },
    payment: {
      options: ["30000"] as PaymentOptionKey[],
    },
  },
  /* 
  [landingFunnelKey.GROUP["promotion_26_g"]]: {
    ...PROMOTION_9900_TEMPLATE,
    paths: { ...PROMOTION_9900_TEMPLATE.paths, home: "/promotion/26-g" },
    payment: {
      options: ["saving"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_26_h"]]: {
    ...PROMOTION_9900_TEMPLATE,
    paths: { ...PROMOTION_9900_TEMPLATE.paths, home: "/promotion/26-h" },
    payment: {
      options: ["saving"] as PaymentOptionKey[],
    },
  }, 
  */
  // 카카오뱅크 메모리얼
  [landingFunnelKey.GROUP["kakao_bank_mt"]]: {
    paths: {
      home: `/?fnk=${landingFunnelKey.GROUP.kakao_bank_mt}`, // 로고 클릭 시 fnk 유지
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP.kakao_bank_mt}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["9900"],
      keys: {
        saving: PRIME["289"].MS99DC2D97MT,
      },
    },
    payment: {
      options: ["100", "saving_mt"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["gas_app"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.gas}&fnk=${landingFunnelKey.GROUP.gas_app}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.gas}&fnk=${landingFunnelKey.GROUP.gas_app}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.gas,
      keys: {
        monthly: PRIME["289"].M1GAS,
        single: PRIME["289"].S1GAS,
        saving: PRIME["289"].MS99DC12D97GAS,
      },
    },
    payment: {
      options: ["100", "saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["kakao_bank"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.kakao_bank}&fnk=${landingFunnelKey.GROUP.kakao_bank}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.kakao_bank}&fnk=${landingFunnelKey.GROUP.kakao_bank}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.kakao_bank,
      keys: {
        monthly: PRIME["289"].M1KB,
        single: PRIME["289"].S1KB,
        saving: PRIME["289"].MS99DC12D97KB,
      },
    },
    // payment: {
    //   // 특별한 혜택 옵션이 없다고 하셨으므로 기본 100원-적금 구조를 그대로 매핑합니다.
    //   // 기본 payment 옵션과 별도로 혜택 관련 뱃지나 번들을 새로 만들지 않기로 협의되었습니다.
    //   options: ["100", "saving"] as PaymentOptionKey[],
    // },
  },
  [landingFunnelKey.GROUP["tdt_9900"]]: {
    ...PROMOTION_3D3_TEMPLATE,
    paths: { ...PROMOTION_3D3_TEMPLATE.paths, home: "" },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["3-d-3"],
      keys: {
        saving: PRIME["289"].MS99DC12D97TDT,
      },
    },
    payment: {
      options: ["saving_tdt"] as PaymentOptionKey[],
    },
  },
  // [기본] 100원/3만원 노혜택 + 9900원 3개월면제+10만할인
  [landingFunnelKey.GROUP["promotion_saving_10"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_saving_10}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_saving_10}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP["100"],
      keys: {
        monthly: PACKAGE_KEY.PRIME["289"].M1,
        single: PACKAGE_KEY.PRIME["289"].S1,
        saving: PACKAGE_KEY.PRIME["289"].MS99DC12D97,
      },
    },
    payment: {
      options: ["100", "saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["ggr_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.ggr}&fnk=${landingFunnelKey.GROUP.ggr_mixed}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.ggr}&fnk=${landingFunnelKey.GROUP.ggr_mixed}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.ggr,
      keys: {
        monthly: PRIME["289"].M1MTGGR,
        single: PRIME["289"].S1MTGGR,
        saving: PRIME["289"].MS99DC2D97MTGGR,
      },
    },
    payment: {
      options: ["100", "saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["mdy_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mdy}&fnk=${landingFunnelKey.GROUP.mdy_mixed}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mdy}&fnk=${landingFunnelKey.GROUP.mdy_mixed}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mdy,
      keys: {
        monthly: PRIME["289"].M1MTMDY,
        single: PRIME["289"].S1MTMDY,
        saving: PRIME["289"].MS99DC2D97MTMDY,
      },
    },
    payment: {
      options: ["100", "saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["okc_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.okc}&fnk=${landingFunnelKey.GROUP.okc_mixed}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.okc}&fnk=${landingFunnelKey.GROUP.okc_mixed}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.okc,
      keys: {
        monthly: PRIME["289"].M1MTOKC,
        single: PRIME["289"].S1MTOKC,
        saving: PRIME["289"].MS99DC2D97MTOKC,
      },
    },
    payment: {
      options: ["100", "saving_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["lpt_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.lpt}&fnk=${landingFunnelKey.GROUP.lpt_mixed}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.lpt}&fnk=${landingFunnelKey.GROUP.lpt_mixed}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.lpt,
      keys: {
        monthly: PRIME["289"].M1MTLPT,
        single: PRIME["289"].S1MTLPT,
        saving: PRIME["289"].MS99DC2D97MTLPT,
      },
    },
    payment: {
      options: ["100", "saving"] as PaymentOptionKey[],
    },
  },
  // 카카오페이 추모테이블 (100원 / 3만원 / 9900원 3개월면제+10만할인 모두 추모)
  [landingFunnelKey.GROUP["kpay_mt_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.kpay}&fnk=${landingFunnelKey.GROUP.kpay_mt_mixed}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.kpay}&fnk=${landingFunnelKey.GROUP.kpay_mt_mixed}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.kpay,
      keys: {
        monthly: PRIME["289"].M1MTKPAY,
        single: PRIME["289"].S1MTKPAY,
        saving: PRIME["289"].MS99DC12D97MTKPAY,
      },
    },
    payment: {
      options: ["100", "saving_mt_10"] as PaymentOptionKey[],
    },
  },
  // 카카오뱅크 추모테이블 (100원 / 3만원 / 9900원 3개월면제+10만할인 모두 추모)
  [landingFunnelKey.GROUP["kbank_mt_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.kbank}&fnk=${landingFunnelKey.GROUP.kbank_mt_mixed}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.kbank}&fnk=${landingFunnelKey.GROUP.kbank_mt_mixed}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.kbank,
      keys: {
        monthly: PRIME["289"].M1MTKBANK,
        single: PRIME["289"].S1MTKBANK,
        saving: PRIME["289"].MS99DC12D97MTKBANK,
      },
    },
    payment: {
      options: ["100", "saving_mt_10"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_279_mt"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        saving: PRIME["279"].MS99MT,
      },
    },
    payment: {
      options: ["saving_mt_279"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_279_mt_single"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt_single"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt_single"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        single: PRIME["279"].S1MT,
      },
    },
    payment: {
      options: ["single_mt_279"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_279_mt_all"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt_all"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt_all"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        monthly: PRIME["279"].M1MT,
        single: PRIME["279"].S1MT,
        saving: PRIME["279"].MS99DC2D97MT,
      },
    },
    payment: {
      options: ["100", "saving_279"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_279_mt_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt_mixed"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_279_mt_mixed"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        monthly: PACKAGE_KEY.PRIME[289].M1DC10,
        single: PACKAGE_KEY.PRIME[289].S1DC10,
        saving: PRIME["289"].MS99DC12D97MT,
      },
    },
    payment: {
      options: ["100", "saving_279"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_259_mt"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_259_mt"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_259_mt"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        saving: PRIME["259"].MS99MT,
      },
    },
    payment: {
      options: ["saving_mt_259"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_289_mt_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_mixed"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_mixed"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        monthly: PACKAGE_KEY.PRIME["289"].M1,
        single: PACKAGE_KEY.PRIME["289"].S1,
        saving: PRIME["289"].MS99DC2D97MT,
      },
    },
    payment: {
      options: ["100", "saving_mt"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_289_mt_all"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_all"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_all"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        monthly: PRIME["289"].M1MT,
        single: PRIME["289"].S1MT,
        saving: PRIME["289"].MS99DC2D97MT,
      },
    },
    payment: {
      options: ["100", "saving"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_289_mt_coupon"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_coupon"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_coupon"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        monthly: PRIME["289"].M1MT,
        saving: PRIME["289"].MS99MT,
      },
      coupon: {
        monthly: {
          key: primeCouponCode.M01DISCOUNT289M1MT,
          description:
            "최초 1개월이 납입 면제됩니다.\n카드를 등록해도 결제되지 않습니다.",
        },
        saving: {
          key: primeCouponCode.M01DISCOUNT289MS99MT,
          description:
            "최초 1개월이 납입 면제됩니다.\n카드를 등록해도 결제되지 않습니다.",
        },
      },
    },
    payment: {
      options: ["100", "saving_mt_zero"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["promotion_289_mt_coupon_tdt"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_coupon_tdt"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["promotion_289_mt_coupon_tdt"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        monthly: PRIME["289"].M1MTTDT,
        saving: PRIME["289"].MS99MTTDT,
      },
      coupon: {
        monthly: {
          key: primeCouponCode.M01DISCOUNT289M1MTTDT,
          description:
            "최초 1개월이 납입 면제됩니다.\n카드를 등록해도 결제되지 않습니다.",
        },
        saving: {
          key: primeCouponCode.M01DISCOUNT289MS99MTTDT,
          description:
            "최초 1개월이 납입 면제됩니다.\n카드를 등록해도 결제되지 않습니다.",
        },
      },
    },
    payment: {
      options: ["100", "saving_mt_zero"] as PaymentOptionKey[],
    },
  },
  // 삼쩜삼 추모테이블 (100원 추모 / 9900원 3개월면제+추모)
  [landingFunnelKey.GROUP["tdt_mt_mixed"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["3-d-3"]}&fnk=${landingFunnelKey.GROUP["tdt_mt_mixed"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["3-d-3"]}&fnk=${landingFunnelKey.GROUP["tdt_mt_mixed"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["3-d-3"],
      keys: {
        monthly: PRIME["289"].M1MTTDT,
        single: PRIME["289"].S1MTTDT,
        saving: PRIME["289"].MS99DC2D97TDT,
      },
    },
    payment: {
      options: ["100", "saving_mt_tdt"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["referral_promotion_05"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["referral_promotion_05"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.mt}&fnk=${landingFunnelKey.GROUP["referral_promotion_05"]}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.mt,
      keys: {
        monthly: PRIME["289"].M1MT,
        single: PRIME["289"].S1MT,
        saving: PRIME["289"].MS99DC2D97MT,
      },
    },
    payment: {
      options: ["100", "saving"] as PaymentOptionKey[],
    },
  },

  [landingFunnelKey.GROUP["rfr_mixed"]]: {
    paths: {
      home: "/referio",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP.rfr}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP.rfr}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP.rfr,
      keys: {
        monthly: PRIME["289"].M1RFR,
        single: PRIME["289"].S1RFR,
        saving: PRIME["289"].MS99DC2D97MTRFR,
      },
    },
  },
  // 홈쇼핑(HSV) 전용 279만원 (100원/3만원 10만원할인, 9900원 10만원할인+3개월면제)
  [landingFunnelKey.GROUP["promotion_279_hsv"]]: {
    paths: {
      home: "",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_279_hsv}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["100"]}&fnk=${landingFunnelKey.GROUP.promotion_279_hsv}`,
    },
    product: {
      type: "PROMOTION",
      code: PRODUCT_GROUP["100"],
      keys: {
        monthly: PRIME["279"].M1DC10HSV,
        single: PRIME["279"].S1DC10HSV,
        saving: PRIME["279"].MS99DC12D97HSV,
      },
    },
    payment: {
      options: ["100", "saving_hsv_279"] as PaymentOptionKey[],
    },
  },
  [landingFunnelKey.GROUP["revisit_30"]]: {
    paths: {
      home: "/revisit30",
      info: "/registration/info",
      "info-trust": "/registration/info",
      verification: `/registration/verification/?productGroup=${PRODUCT_GROUP["100"]}`,
      "product-intro": `/registration/product-intro/?productGroup=${PRODUCT_GROUP["100"]}`,
    },
    product: {
      type: "GROUP",
      code: PRODUCT_GROUP["100"],
      keys: {
        monthly: PACKAGE_KEY.PRIME[289].M1DC10,
        single: PACKAGE_KEY.PRIME[289].S1DC10,
        saving: PRIME["289"].MS99DC12D97MT,
      },
    },
  },
};

const DEFAULT_PAYMENT_OPTIONS: PaymentOptionKey[] = ["100", "saving"];

export const getPaymentOptions = (funnelKey?: string): PaymentOptionKey[] => {
  if (!funnelKey) return DEFAULT_PAYMENT_OPTIONS;
  return (
    funnelDetailValues[funnelKey]?.payment?.options ?? DEFAULT_PAYMENT_OPTIONS
  );
};

export type FunnelKey = keyof typeof funnelDetailValues;

export const funnelDetailValuesKey = Object.fromEntries(
  (Object.keys(funnelDetailValues) as FunnelKey[]).map((k) => [k, k])
) as { [K in FunnelKey]: K };
